import { Analytics } from '@cobliteam/analytics';
import { BlueprintProvider as BlueprintProviderLib } from '@cobliteam/blueprint-react-renderer';
import { useLanguage } from '@cobliteam/i18n';
import Environment from '@cobliteam/react-environments';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { routeRegistry } from './config/navigation';
import BlueprintRequester from './config/requester';

interface BlueprintProviderProps {
  children: React.ReactNode;
}

const toSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
const env = Environment.getInstance().getEnvironment();

const BlueprintProvider = ({ children }: BlueprintProviderProps) => {
  const requester = useMemo(() => new BlueprintRequester({ host: env.blueprintApiHost }), []);
  const { currentLanguage: language } = useLanguage();
  const history = useHistory();
  const screen = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  };

  const onTrackEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => Analytics.track(eventName, { ...properties, source: 'blueprint' }), []);

  const onNavigate = useCallback(
    (path: string, params?: Record<string, unknown>, options?: { target?: string }) => {
      const isExternalTarget = options?.target === '_blank';

      const searchParams = new URLSearchParams();
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            const paramKey = isExternalTarget ? toSnakeCase(key) : key;
            searchParams.append(paramKey, String(value));
          }
        }
      }

      const qs = searchParams.toString();
      const fullPath = qs ? `${path}?${qs}` : path;

      if (isExternalTarget) {
        window.open(fullPath, '_blank', 'noopener,noreferrer');
      } else {
        history.push(fullPath);
      }
    },
    [history]
  );

  return (
    <BlueprintProviderLib
      config={{
        navigation: {
          routeRegistry,
          onNavigate,
        },
        requester,
        language,
        screen,
        onTrackEvent,
      }}
    >
      {children}
    </BlueprintProviderLib>
  );
};

export { BlueprintProvider };
